import { cUtil }    from './../../../functions/util';
import { getPrice } from '../../../objects/price/get-price';
import {
  platterPrice,
  arrangeCount,
  parseComponents,
  componentsToJson,
  PlatterPrice
} from '../../../objects/platter/platter-price';

// Auswahl der Komponenten einer Platte mit Personenrechner, siehe objects/platter/platter.php und
// spec/fondue.md

const STEP_GRAMS = 50;

interface PlatterRow {
  data         : PlatterComponentData;
  isVisible    : boolean;
  reference    : number; // Ausgangsverteilung für den Personenrechner, siehe setReference()
  div_row      : HTMLDivElement;
  label_name   : HTMLLabelElement;
  span_offer   : HTMLSpanElement;
  div_price    : HTMLDivElement;
  del_priceOld : HTMLModElement;
  span_price   : HTMLSpanElement;
  div_stepper  : HTMLDivElement;
  button_minus : HTMLButtonElement;
  div_field    : HTMLDivElement;
  input_grams  : HTMLInputElement;
  span_unit    : HTMLSpanElement;
  button_plus  : HTMLButtonElement;
}

export class cProductItemPlatter {

  fieldset_platter    = document.createElement('fieldset');
  div_persons         = document.createElement('div');
  label_persons       = document.createElement('label');
  span_personsInfo    = document.createElement('span');
  div_personsStepper  = document.createElement('div');
  button_personsMinus = document.createElement('button');
  input_persons       = document.createElement('input');
  button_personsPlus  = document.createElement('button');
  div_rows            = document.createElement('div');
  div_arrange         = document.createElement('div');
  input_arrange       = document.createElement('input');
  label_arrange       = document.createElement('label');
  span_arrangePrice   = document.createElement('span');
  div_error           = document.createElement('div');

  rows      : PlatterRow[] = [];
  product   : ProductData;
  parentObj : DateFieldTop;
  context   : 'single-product' | 'order-list' | 'cart';
  date      : string = '';
  arrangePrice       = 0; // Preis von "Auf Platte anrichten" pro angebrochenes Kg am Datum, siehe update()

  constructor(
    item      : OrderListItem,
    product   : ProductData,
    parent    : HTMLElement,
    parentObj : DateFieldTop,
    context   : 'single-product' | 'order-list' | 'cart'
  ) {
    const { __, _x, _n, sprintf } = wp.i18n as i18n;

    this.product   = product;
    this.parentObj = parentObj;
    this.context   = context;

    // ---- Structure ----
    parent.append(this.fieldset_platter);
      this.fieldset_platter.append(this.div_persons);
        this.div_persons.append(this.label_persons);
        this.div_persons.append(this.span_personsInfo);
        this.div_persons.append(this.div_personsStepper);
          this.div_personsStepper.append(this.button_personsMinus);
          this.div_personsStepper.append(this.input_persons);
          this.div_personsStepper.append(this.button_personsPlus);
      this.fieldset_platter.append(this.div_rows);
      this.fieldset_platter.append(this.div_arrange);
        this.div_arrange.append(this.input_arrange);
        this.div_arrange.append(this.label_arrange);
        this.div_arrange.append(this.span_arrangePrice);
      this.fieldset_platter.append(this.div_error);

    const components = parseComponents(item.components);
    for (const data of product.platter) {
      const grams = components.find(component => component.sku === data.sku)?.grams ?? 0;
      this.rows.push(this.createRow(data, grams));
    }

    // ---- Classes ----
    this.css('fieldset_platter');
    this.css('div_persons');
    this.css('label_persons');
    this.css('span_personsInfo');
    this.css('div_personsStepper');
    this.css('input_persons');
    this.css('div_rows');
    this.css('div_arrange');
    this.css('input_arrange');
    this.css('label_arrange');
    this.css('span_arrangePrice');
    this.css('div_error');
    this.button_personsMinus.classList.add('button_platterMinus');
    this.button_personsPlus .classList.add('button_platterPlus');

    // ---- Texts ----
    // Kein sichtbarer Titel, der Name bleibt für Screenreader
    this.fieldset_platter.setAttribute('aria-label', __('Zusammensetzung', 'custom'));
    this.label_persons .textContent = __('Personen'       , 'custom');
    this.button_personsMinus.setAttribute('aria-label', __('Eine Person weniger', 'custom'));
    this.button_personsPlus .setAttribute('aria-label', __('Eine Person mehr'   , 'custom'));
    this.label_arrange    .textContent = __('Auf Platte anrichten', 'custom');

    // ---- Other ----
    cUtil.labelIds(this.label_persons, this.input_persons);
    cUtil.labelIds(this.label_arrange, this.input_arrange);
    this.input_arrange      .type      = 'checkbox';
    this.input_arrange      .checked   = item.arranged;
    this.input_persons      .type      = 'text';
    this.input_persons      .inputMode = 'numeric';
    this.button_personsMinus.type      = 'button';
    this.button_personsPlus .type      = 'button';

    // ---- Events ----
    this.button_personsMinus.addEventListener('click', () => {
      this.setPersons(this.getPersons() - 1);
    });

    this.button_personsPlus.addEventListener('click', () => {
      this.setPersons(this.getPersons() + 1);
    });

    // Erst beim Verlassen des Feldes, sonst verteilte schon die erste Ziffer von "12" neu
    this.input_persons.addEventListener('change', () => {
      this.setPersons(Number(this.input_persons.value.trim()));
    });

    this.input_persons.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      this.setPersons(Number(this.input_persons.value.trim()));
    });

    // Ändert nur den Preis, die Ausgangsverteilung des Personenrechners bleibt
    this.input_arrange.addEventListener('change', () => {
      this.change();
    });

    // ---- Init ----
    if (product.platterGramsPerPerson === 0) this.div_persons.classList.add('c-hidden');
    this.update(item.delivery_date);
  }

  createRow(data: PlatterComponentData, grams: number): PlatterRow {
    const { __, _x, _n, sprintf } = wp.i18n as i18n;

    const row: PlatterRow = {
      data         : data,
      isVisible    : true,
      reference    : grams,
      div_row      : document.createElement('div'),
      label_name   : document.createElement('label'),
      span_offer   : document.createElement('span'),
      div_price    : document.createElement('div'),
      del_priceOld : document.createElement('del'),
      span_price   : document.createElement('span'),
      div_stepper  : document.createElement('div'),
      button_minus : document.createElement('button'),
      div_field    : document.createElement('div'),
      input_grams  : document.createElement('input'),
      span_unit    : document.createElement('span'),
      button_plus  : document.createElement('button'),
    };

    // ---- Structure ----
    this.div_rows.append(row.div_row);
      row.div_row.append(row.label_name);
      row.div_row.append(row.span_offer);
      row.div_row.append(row.div_price);
        row.div_price.append(row.del_priceOld);
        row.div_price.append(row.span_price);
      row.div_row.append(row.div_stepper);
        row.div_stepper.append(row.button_minus);
        row.div_stepper.append(row.div_field);
          row.div_field.append(row.input_grams);
          row.div_field.append(row.span_unit);
        row.div_stepper.append(row.button_plus);

    // ---- Classes ----
    row.div_row     .classList.add('div_platterRow');
    row.label_name  .classList.add('label_platterName');
    row.span_offer  .classList.add('span_platterOffer');
    row.div_price   .classList.add('div_platterPrice');
    row.del_priceOld.classList.add('del_platterPriceOld');
    row.span_price  .classList.add('span_platterPrice');
    row.div_stepper .classList.add('div_platterStepper');
    row.button_minus.classList.add('button_platterMinus');
    row.div_field   .classList.add('div_platterField');
    row.input_grams .classList.add('input_platterGrams');
    row.span_unit   .classList.add('span_platterUnit');
    row.button_plus .classList.add('button_platterPlus');

    // ---- Texts ----
    row.label_name.textContent = data.name;
    row.span_unit .textContent = 'g';
    row.button_minus.setAttribute('aria-label', __('50g weniger', 'custom'));
    row.button_plus .setAttribute('aria-label', __('50g mehr'   , 'custom'));

    // ---- Other ----
    cUtil.labelIds(row.label_name, row.input_grams);
    row.input_grams .type      = 'text';
    row.input_grams .inputMode = 'numeric';
    this.setGrams(row, grams);
    row.button_minus.type      = 'button';
    row.button_plus .type      = 'button';

    // ---- Events ----
    row.button_minus.addEventListener('click', () => {
      const grams = this.getGrams(row);
      this.setGrams(row, Math.max(0, Math.ceil(grams / STEP_GRAMS) * STEP_GRAMS - STEP_GRAMS));
      this.setReference();
      this.change();
    });

    row.button_plus.addEventListener('click', () => {
      const grams = this.getGrams(row);
      this.setGrams(row, Math.floor(grams / STEP_GRAMS) * STEP_GRAMS + STEP_GRAMS);
      this.setReference();
      this.change();
    });

    row.input_grams.addEventListener('input', () => {
      this.fitGrams(row);
      this.setReference();
      this.change();
    });

    row.input_grams.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') e.preventDefault();
    });

    return row;
  }

  // Preise und Markierungen für das Lieferdatum. Eine Komponente ohne Preis am Datum fällt weg.
  update(date: string): void {
    const { __, _x, _n, sprintf } = wp.i18n as i18n;

    this.date = date;

    for (const row of this.rows) {
      const priceObj = getPrice(date, 'effective', row.data.prices);
      row.isVisible  = priceObj.productVisible;

      if (!row.isVisible) {
        row.div_row.classList.add('c-hidden');
        this.setGrams(row, 0);
        continue;
      }
      row.div_row.classList.remove('c-hidden');

      const toDate  = cUtil.isoToCh(priceObj.toDate);
      const dateStr =
        (toDate !== '01.01.1970') && (toDate !== '31.12.9999')
        ? ' ' + __('bis', 'custom') + ' ' + toDate
        : '';

      // Bei einem Angebot der reguläre Preis durchgestrichen davor, wie in displayAmounts()
      const origPriceObj = getPrice(date, 'regular', row.data.prices);
      const showOrig     = priceObj.isOnSale && !priceObj.isIndvidualPrice && origPriceObj.price !== 0;

      row.del_priceOld.textContent = showOrig ? `CHF ${(origPriceObj.price / 10).toFixed(2)}` : '';
      row.span_price  .textContent = `CHF ${(priceObj.price / 10).toFixed(2)} / 100g`;
      row.span_offer.textContent =
        priceObj.isIndvidualPrice ? __('Offerte', 'custom') + dateStr :
        priceObj.isOnSale         ? __('Angebot', 'custom') + dateStr :
        /*else*/                    '';

      row.div_row.classList.toggle('c-individual-price', priceObj.isIndvidualPrice);
      row.div_row.classList.toggle('c-offer'           , !priceObj.isIndvidualPrice && priceObj.isOnSale);
    }

    // Auf Platte anrichten: der Preis pro angebrochenes Kg am Datum wie in \c\Platter\arrangePrice().
    // Ohne Preis fällt die Checkbox weg, wie eine Komponente ohne Preis.
    const arrangeObj  = getPrice(date, 'effective', this.product.platterArrangePrices);
    this.arrangePrice = arrangeObj.productVisible ? arrangeObj.price : 0;
    this.div_arrange.classList.toggle('c-hidden', !arrangeObj.productVisible);
    if (!arrangeObj.productVisible) this.input_arrange.checked = false;

    this.showPersons();
    this.showArrangePrice();
  }

  // "+ CHF 10.00 (2 × 5.00)" für 1300g. Ohne Gramm nur der Preis pro angebrochenes Kg.
  showArrangePrice(): void {
    const { __, _x, _n, sprintf } = wp.i18n as i18n;

    const count    = arrangeCount(this.getTotalGrams());
    const perKgStr = this.arrangePrice.toFixed(2);

    this.span_arrangePrice.textContent =
      count === 0
      ? sprintf(__('+ CHF %s pro angebrochenes Kg', 'custom'), perKgStr)
      : `+ CHF ${(count * this.arrangePrice).toFixed(2)} (${count} × ${perKgStr})`;
  }

  // ---- Personenrechner ----

  getTotalGrams(): number {
    return this.getComponents().reduce((sum, component) => sum + component.grams, 0);
  }

  // Gesamtmenge / Gewicht pro Person, kaufmännisch gerundet
  showPersons(): void {
    const { __, _x, _n, sprintf } = wp.i18n as i18n;

    const perPerson = this.product.platterGramsPerPerson;
    const total     = this.getTotalGrams();

    this.input_persons   .value       = perPerson > 0 ? String(Math.floor(total / perPerson + 0.5)) : '';
    this.span_personsInfo.textContent = sprintf(__('Total %sg, %sg pro Person', 'custom'), String(total), String(perPerson));
  }

  // Die angezeigte Zahl, oder die berechnete, wenn im Feld etwas Ungültiges steht
  getPersons(): number {
    const value = Number(this.input_persons.value.trim());
    if (this.input_persons.value.trim() !== '' && Number.isInteger(value) && value >= 0) return value;

    const perPerson = this.product.platterGramsPerPerson;
    return perPerson > 0 ? Math.floor(this.getTotalGrams() / perPerson + 0.5) : 0;
  }

  // Auf der Produktseite und im Warenkorb mindestens 1 Person, in der Bestellliste 0, was alles auf 0g setzt
  setPersons(persons: number): void {
    if (!Number.isInteger(persons) || this.product.platterGramsPerPerson === 0) {
      this.showPersons();
      return;
    }
    const min = this.context === 'order-list' ? 0 : 1;
    this.distribute(Math.max(min, persons) * this.product.platterGramsPerPerson);
    this.change();
  }

  /*
   * Die Ausgangsverteilung: die Gramm beim Öffnen der Seite, danach nach jeder Änderung von Hand.
   * Eine Änderung der Personenzahl ändert sie nicht, sonst setzten sich die Rundungen von Schritt zu
   * Schritt fort.
   */
  setReference(): void {
    for (const row of this.rows) row.reference = this.getGrams(row);
  }

  /*
   * Verteilt die Gesamtmenge im Verhältnis der Ausgangsverteilung, in 50g-Schritten. Eine Komponente
   * mit 0g bleibt bei 0g, jede andere behält mindestens 50g: Wessen Anteil darunter fiele, bekommt
   * 50g, der Rest geht im Verhältnis an die übrigen. Was beim Runden übrig bleibt, geht an die
   * Komponenten mit dem grössten Rest, bei gleichem Rest an die erste in der Reihenfolge der
   * Konfiguration (componentSkus() in platter.php). Stehen alle auf 0g, gilt das Verhältnis der
   * Vorauswahl.
   */
  distribute(targetGrams: number): void {
    const rows = this.rows.filter(row => row.isVisible);

    if (targetGrams === 0) {
      for (const row of rows) this.setGrams(row, 0);
      return;
    }

    const current = rows.map(row => row.reference);
    const base    = current.every(grams => grams === 0) ? rows.map(row => row.data.grams) : current;
    const units   = rows.map(() => 0);
    const shareOf = (i: number, free: number[], remaining: number): number => {
      const baseTotal = free.reduce((sum, j) => sum + (base[j] ?? 0), 0);
      return baseTotal === 0 ? 0 : (base[i] ?? 0) / baseTotal * remaining;
    };

    let remaining = Math.round(targetGrams / STEP_GRAMS);
    let free      = base.flatMap((grams, i) => grams > 0 ? [i] : []);

    // Mindestens 50g
    for (;;) {
      const small = free.filter(i => shareOf(i, free, remaining) < 1);
      if (small.length === 0) break;
      for (const i of small) units[i] = 1;
      remaining -= small.length;
      free       = free.filter(i => !small.includes(i));
    }

    // Im Verhältnis, Rundungsrest an die grössten Reste, bei Gleichstand nach Reihenfolge
    const shares = free.map(i => ({ i, share: shareOf(i, free, remaining) }));
    for (const { i, share } of shares) units[i] = Math.floor(share);

    let rest = remaining - free.reduce((sum, i) => sum + (units[i] ?? 0), 0);
    const byFraction = shares
      .map(({ i, share }) => ({ i, fraction: share - Math.floor(share) }))
      .sort((a, b) => (b.fraction - a.fraction) || (a.i - b.i));
    for (const { i } of byFraction) {
      if (rest <= 0) break;
      units[i] = (units[i] ?? 0) + 1;
      rest    -= 1;
    }

    rows.forEach((row, i) => this.setGrams(row, (units[i] ?? 0) * STEP_GRAMS));
  }

  setGrams(row: PlatterRow, grams: number): void {
    row.input_grams.value = String(grams);
    this.fitGrams(row);
  }

  // Das Feld so breit wie die Zahl, damit Zahl und Einheit zusammen in der Mitte stehen
  fitGrams(row: PlatterRow): void {
    row.input_grams.style.width = `${Math.max(1, row.input_grams.value.length)}ch`;
  }

  getGrams(row: PlatterRow): number {
    const value = Number(row.input_grams.value.trim());
    return Number.isInteger(value) && value >= 0 ? value : 0;
  }

  // Die Komponenten mit Preis und gültiger Menge über 0 g, in der Reihenfolge der Konfiguration
  getComponents(): PlatterComponent[] {
    return this.rows
      .filter(row => row.isVisible)
      .map(row => ({ sku: row.data.sku, grams: this.getGrams(row) }))
      .filter(component => component.grams > 0 && component.grams % STEP_GRAMS === 0);
  }

  // Ohne Auswahl, etwa in der Bestellliste, gelten Durchschnitt und Stückpreis der Vorauswahl, wie
  // in \c\Platter\amounts(). Die Menge bleibt 0, damit auch der Wert.
  calculate(): PlatterPrice {
    const arrange    = this.isArranged() ? this.arrangePrice : 0;
    const components = this.getComponents();
    if (components.length > 0) return platterPrice(components, this.product.platter, this.date, arrange);

    const defaults = this.rows
      .filter(row => row.isVisible)
      .map(row => ({ sku: row.data.sku, grams: row.data.grams }));
    const price = platterPrice(defaults, this.product.platter, this.date, arrange);
    return { totalKg: 0, average: price.average, total: price.total };
  }

  isArranged(): boolean {
    return this.input_arrange.checked;
  }

  isEmpty(): boolean {
    return this.getComponents().length === 0;
  }

  validate(silent: 'silent'|'' = ''): boolean {
    const { __, _x, _n, sprintf } = wp.i18n as i18n;

    const format = /^\s*[0-9]*\s*$/;
    let errorMsg = '';

    for (const row of this.rows) {
      const value   = row.input_grams.value;
      const isValid = !row.isVisible || (format.test(value) && Number(value) % STEP_GRAMS === 0);
      row.div_field.classList.toggle('c--error-marker', silent !== 'silent' && !isValid);
      if (!isValid && errorMsg === '') {
        errorMsg = __('Bitte geben Sie die Mengen in Schritten von 50g ein.', 'custom');
      }
    }

    // In der Bestellliste darf die Platte leer bleiben
    if (errorMsg === '' && this.context !== 'order-list' && this.isEmpty()) {
      errorMsg = __('Bitte wählen Sie mindestens eine Komponente.', 'custom');
    }

    const hasError = errorMsg !== '';

    if (silent !== 'silent' && hasError) {
      this.div_error.textContent = errorMsg;
      this.div_error.classList.add('c--error');
    }
    else {
      this.div_error.textContent = '';
      this.div_error.classList.remove('c--error');
    }

    return !hasError;
  }

  getData(): string {
    return componentsToJson(this.getComponents());
  }

  setTabIndex(index: number): void {
    this.button_personsMinus.tabIndex = index;
    this.input_persons      .tabIndex = index;
    this.button_personsPlus .tabIndex = index;
    for (const row of this.rows) {
      row.button_minus.tabIndex = index;
      row.input_grams .tabIndex = index;
      row.button_plus .tabIndex = index;
    }
    this.input_arrange.tabIndex = index;
  }

  change(): void {
    this.showPersons();
    this.showArrangePrice();
    this.parentObj.changeEvent('cProductItemPlatter_change');
  }

  css<K extends keyof cProductItemPlatter>(propertyName: K): void {
    const property = this[propertyName];
    if (! (property instanceof HTMLElement)) return;
    property.classList.add(propertyName);
  }
}
